import React, { useEffect, useState } from "react";
import {
  FaUserCircle,
  FaShieldAlt,
  FaCamera,
  FaSignOutAlt,
  FaFireAlt,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";

import "../styles/Profile.css";
import { API_ENDPOINTS } from "../config/api";
import { useHomam } from "../context/HomamContext";

const API = API_ENDPOINTS.USER_PROFILE;

const Profile = () => {
  const navigate = useNavigate();
  const { isHomamActive } = useHomam();

  const [activeTab, setActiveTab] = useState("profile");

  const [profileImage, setProfileImage] = useState("/images/saibaba1.jpg");

  const [user, setUser] = useState({
    username: "",
    email: "",
    mobile: "",
    location: "",
    bio: "",
    password: "",
    newPassword: "",
  });

  const [homamBookings, setHomamBookings] = useState([]);
  const [homamLoading, setHomamLoading] = useState(false);

  /* ================= LOAD USER ================= */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          navigate("/account");
          return;
        }

        const res = await fetch(`${API}/me`, {
          method: "GET",
          headers: {
            Authorization: token,
          },
        });

        const data = await res.json();

        if (res.ok) {
          setUser((prev) => ({
            ...prev,
            username: data.fullName || "",
            email: data.email || "",
            mobile: data.mobile || "",
            location: data.location || "",
            bio: data.bio || "",
          }));

          if (data.profileImage) {
            setProfileImage(data.profileImage);
          }
        }
      } catch (err) {
        console.log("Fetch user error:", err);
      }
    };

    fetchUser();
  }, [navigate]);

  /* ================= HANDLE INPUT ================= */
  const handleChange = (e) => {
    setUser((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  /* ================= IMAGE UPLOAD + SAVE ================= */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // preview instantly
      const previewUrl = URL.createObjectURL(file);
      setProfileImage(previewUrl);

      const formData = new FormData();
      formData.append("image", file);

      // upload to backend
      const uploadRes = await fetch(API_ENDPOINTS.UPLOAD, {
        method: "POST",
        body: formData,
      });

      const uploadData = await uploadRes.json();

      if (!uploadRes.ok) {
        alert("Image upload failed");
        return;
      }

      const imageUrl = uploadData.imageUrl;

      setProfileImage(imageUrl);

      // save to DB
      const token = localStorage.getItem("token");

      await fetch(`${API}/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          profileImage: imageUrl,
        }),
      });
    } catch (err) {
      console.log("Image upload error:", err);
    }
  };

  /* ================= SAVE PROFILE ================= */
  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API}/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          fullName: user.username,
          email: user.email,
          mobile: user.mobile,
          location: user.location,
          bio: user.bio,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        alert("Profile updated successfully!");
        navigate("/");
      } else {
        alert(data.message || "Update failed");
      }
    } catch (err) {
      console.log("Save error:", err);
    }
  };

  /* ================= PASSWORD UPDATE ================= */
  const handlePasswordUpdate = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API}/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          oldPassword: user.password,
          newPassword: user.newPassword,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        alert("Password updated successfully!");
        navigate("/");
      } else {
        alert(data.message || "Password update failed");
      }
    } catch (err) {
      console.log("Password error:", err);
    }
  };

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/account");
  };

  /* ================= LOAD HOMAM BOOKINGS ================= */
  const fetchHomamBookings = async () => {
    try {
      setHomamLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(
        API_ENDPOINTS.HOMAM_MY_BOOKINGS || `${API_ENDPOINTS.API_BASE}/homam/my-bookings`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setHomamBookings(Array.isArray(data.bookings) ? data.bookings : []);
      }
    } catch (err) {
      console.error("Fetch devotee homam bookings error:", err);
    } finally {
      setHomamLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "homam") {
      fetchHomamBookings();
    }
  }, [activeTab]);

  return (
    <div className="profile-page">

      {/* HEADER */}
      <div className="profile-header">
        <div className="header-top">
          <div>
            <h1>My Account</h1>
            <p>Manage profile, security settings, and view your sacred Homam bookings</p>
          </div>
        </div>
      </div>

      <div className="profile-layout">

        {/* SIDEBAR */}
        <div className="profile-sidebar">

          <div className="sidebar-profile">

            <img
              src={profileImage}
              alt="user"
              className="profile-image"
            />

            <label className="upload-btn">
              <FaCamera /> Upload Photo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageUpload}
              />
            </label>

            <h2>{user.username || "Devotee"}</h2>
            <p>{user.email}</p>

          </div>

          <div className="sidebar-menu">

            <button
              className={activeTab === "profile" ? "active" : ""}
              onClick={() => setActiveTab("profile")}
            >
              <FaUserCircle /> Profile
            </button>

            {isHomamActive && (
              <button
                className={activeTab === "homam" ? "active" : ""}
                onClick={() => setActiveTab("homam")}
              >
                <FaFireAlt /> My Homam Bookings
              </button>
            )}

            <button
              className={activeTab === "security" ? "active" : ""}
              onClick={() => setActiveTab("security")}
            >
              <FaShieldAlt /> Security
            </button>

          </div>

          <button className="logout-btn sidebar-logout" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>

        </div>

        {/* CONTENT */}
        <div className="profile-content">

          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div className="profile-card">

              <h2>Profile Information</h2>

              <div className="profile-form">

                <input
                  name="username"
                  value={user.username}
                  onChange={handleChange}
                  placeholder="Username"
                />

                <input
                  name="email"
                  value={user.email}
                  onChange={handleChange}
                  placeholder="Email"
                />

                <input
                  name="mobile"
                  value={user.mobile}
                  onChange={handleChange}
                  placeholder="Mobile"
                />

                <input
                  name="location"
                  value={user.location}
                  onChange={handleChange}
                  placeholder="Location"
                />

                <textarea
                  name="bio"
                  value={user.bio}
                  onChange={handleChange}
                  placeholder="Bio"
                  rows="4"
                />

                <button className="save-btn" onClick={handleSave}>
                  Save Changes
                </button>

              </div>

            </div>
          )}

          {/* HOMAM BOOKINGS TAB */}
          {activeTab === "homam" && (
            <div className="profile-card homam-bookings-card">
              <div className="homam-bookings-header">
                <div>
                  <h2>🔱 My Nakshatra Homam Bookings</h2>
                  <p>Track your sacred Homam bookings, Seva plans, and payment verification status</p>
                </div>
                <button
                  type="button"
                  className="homam-book-new-btn"
                  onClick={() => navigate('/nakshatra-homam')}
                >
                  + Book New Homam
                </button>
              </div>

              {homamLoading ? (
                <div className="homam-profile-loading">
                  <div className="homam-spinner" />
                  <p>Loading your sacred Homam bookings...</p>
                </div>
              ) : homamBookings.length === 0 ? (
                <div className="homam-profile-empty">
                  <div className="empty-diya-icon">🪔</div>
                  <h3>No Homam Registrations Yet</h3>
                  <p>You have not registered for any 27 Nakshatra Maha Homam dates yet.</p>
                  <button
                    className="homam-cta-btn"
                    onClick={() => navigate('/nakshatra-homam')}
                  >
                    Explore 27 Stars & Register
                  </button>
                </div>
              ) : (
                <div className="homam-bookings-list">
                  {homamBookings.map((booking) => (
                    <div key={booking._id} className="devotee-booking-item">
                      {/* Top Bar with ID and Status */}
                      <div className="devotee-booking-top">
                        <div className="devotee-booking-id-tag">
                          <span className="booking-id-label">Booking ID:</span>
                          <span className="booking-id-val">{booking.bookingId}</span>
                        </div>
                        <span className={`booking-status-pill status-${booking.paymentStatus?.toLowerCase() || 'pending'}`}>
                          {booking.paymentStatus === 'Verified'
                            ? '✓ Payment Verified & Confirmed'
                            : booking.paymentStatus === 'Rejected'
                            ? '✕ Payment Rejected'
                            : '● Verification Pending'}
                        </span>
                      </div>

                      {/* Star and Date details */}
                      <div className="devotee-booking-grid">
                        <div className="booking-info-block">
                          <label>Honored Nakshatra (Star)</label>
                          <h4>Day {booking.nakshatraId}: {booking.nakshatraNameEn} {booking.nakshatraNameTa ? `(${booking.nakshatraNameTa})` : ''}</h4>
                          <p>{booking.homamDate || booking.preferredDay}</p>
                        </div>

                        <div className="booking-info-block">
                          <label>Seva Plan & Dakshina</label>
                          <h4>{booking.planName}</h4>
                          <p className="price-tag">₹{((booking.planAmount && booking.planAmount > 1) ? booking.planAmount : 1001).toLocaleString('en-IN')}</p>
                        </div>

                        <div className="booking-info-block">
                          <label>12-Digit UTR Number</label>
                          <code className="utr-code">{booking.utrNumber || 'Pending'}</code>
                          <span className="booking-time-label">
                            Registered: {new Date(booking.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>

                      {/* Family Members list if any */}
                      {booking.familyMembers && booking.familyMembers.length > 0 && (
                        <div className="devotee-family-section">
                          <label>Family Members for Sacred Homam ({booking.familyMembers.length}):</label>
                          <div className="devotee-family-chips">
                            {booking.familyMembers.map((m, idx) => (
                              <span key={idx} className="devotee-family-chip">
                                👤 {m.name} <small>({m.relationship}{m.nakshatra ? ` • ${m.nakshatra}` : ''})</small>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {booking.rejectionReason && (
                        <div className="devotee-rejection-alert">
                          <strong>Verification Note:</strong> {booking.rejectionReason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SECURITY TAB */}
          {activeTab === "security" && (
            <div className="profile-card">

              <h2>Security Settings</h2>

              <div className="profile-form">

                <input
                  type="password"
                  name="password"
                  value={user.password}
                  onChange={handleChange}
                  placeholder="Current Password"
                />

                <input
                  type="password"
                  name="newPassword"
                  value={user.newPassword}
                  onChange={handleChange}
                  placeholder="New Password"
                />

                <button className="save-btn" onClick={handlePasswordUpdate}>
                  Update Password
                </button>

              </div>

            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default Profile;